/* Shared pointer-event drag-and-drop layer (D1).

   The foundation for the drag/drop modernization roadmap
   (docs/ui_drag_drop_modernization_plan.md). One module so every
   draggable surface — panel move (today), drag-to-pin / reorder /
   drag-to-compare (D2+) — shares a single, tested lifecycle instead of
   each re-implementing pointer math.

   Why pointer events (not HTML5 DnD, not a library): the HTML5 DnD API
   has unstylable ghosts and weak mobile support; dnd-kit is React-only;
   SortableJS fights cross-surface "drop onto a tab" flows. Figma /
   Linear / Notion all use pointer events — and so did our existing
   `beginPanelDrag`, which this module generalizes.

   Two lifecycles, one controller:
     • MOVE mode — reposition the source element directly (panel drag).
       No ghost, no drop targets. `onMove` writes the new position.
     • DROP mode — a ghost clone follows the pointer while the source
       stays put (dimmed); the controller hit-tests a DropTargetRegistry
       and fires enter/leave/drop on targets. ESC cancels.

   Both run through `beginDrag(event, source, options)`; the options
   chosen decide which lifecycle you get.

   D1 ships the controller + registry + ghost helper and refactors the
   panel move onto it (MOVE mode, no behavior change). The DROP-mode
   primitives are consumed starting in D2. */

const DEFAULT_THRESHOLD = 3;
const GHOST_CLASS = 'lb-dnd-ghost';
const DROP_ACTIVE_CLASS = 'is-drop-active';

export interface DragContext {
	// The element the drag started from.
	source: HTMLElement;
	// Pointer origin (the initiating pointerdown).
	startX: number;
	startY: number;
	// Live pointer position (updated each move).
	clientX: number;
	clientY: number;
	// Live pointer delta from origin.
	deltaX: number;
	deltaY: number;
	// The ghost clone, if DROP mode created one.
	ghost: HTMLElement | null;
	// The drop target currently under the pointer (DROP mode), or null.
	activeTarget: DropTarget | null;
	// The most recent pointer event for this phase.
	event: PointerEvent;
}

export interface DropTarget {
	// The element that accepts drops; used for hit-testing.
	element: HTMLElement;
	// Optional predicate gating whether this target accepts the current
	// drag (e.g. by a data-type carried on the source). Defaults to
	// accept-all when omitted.
	accepts?: (ctx: DragContext) => boolean;
	// Fired when the pointer enters this target during a drag (after the
	// previous target, if any, gets onLeave).
	onEnter?: (ctx: DragContext) => void;
	// Fired when the pointer leaves this target (or the drag ends while
	// this target was active).
	onLeave?: (ctx: DragContext) => void;
	// Fired when the drag is dropped over this target.
	onDrop?: (ctx: DragContext) => void;
}

/* A registry of live drop targets. A consumer (D2+) registers targets
   when a drag-capable surface mounts and unregisters on teardown; the
   DragController hit-tests against it each move. Kept as an explicit
   registry rather than a DOM scan so targets can carry behavior
   (predicates, callbacks) and so hit-testing is O(registered), not
   O(DOM). */
export class DropTargetRegistry {
	private targets: DropTarget[] = [];

	// Register a target. Returns an unregister thunk.
	register(target: DropTarget): () => void {
		this.targets.push(target);
		return () => {
			const idx = this.targets.indexOf(target);
			if (idx >= 0) {
				this.targets.splice(idx, 1);
			}
		};
	}

	clear(): void {
		this.targets = [];
	}

	// The last-registered target whose element contains the pointer and
	// whose `accepts` predicate (if any) passes. Last-registered wins so
	// a target mounted later (visually on top) takes precedence.
	hitTest(ctx: DragContext): DropTarget | null {
		for (let i = this.targets.length - 1; i >= 0; --i) {
			const target = this.targets[i];
			const rect = target.element.getBoundingClientRect();
			const inside = ctx.clientX >= rect.left
				&& ctx.clientX <= rect.right
				&& ctx.clientY >= rect.top
				&& ctx.clientY <= rect.bottom;
			if (!inside) {
				continue;
			}
			if (target.accepts && !target.accepts(ctx)) {
				continue;
			}
			return target;
		}
		return null;
	}
}

export interface DragOptions {
	// Pixels of pointer movement before a drag "starts" (hysteresis), so
	// a click with a tiny jitter doesn't trigger a drag. Default 3.
	threshold?: number;
	// DROP mode: a registry to hit-test against each move. Omit for MOVE
	// mode (panel drag), where nothing is being dropped onto a target.
	registry?: DropTargetRegistry;
	// DROP mode: factory that builds a ghost clone following the pointer.
	// Use `defaultGhost` or a custom builder. Omit for MOVE mode.
	createGhost?: (source: HTMLElement) => HTMLElement;
	// Whether ESC (and pointercancel) cancels the drag. Default true.
	// MOVE mode (panel drag) sets false so ESC keeps its existing meaning
	// (drawer toggle / panel close) during a move.
	escCancels?: boolean;
	// `document.body` cursor applied while dragging (e.g. 'grabbing').
	cursor?: string;
	// Fired once when the drag crosses the threshold (drag truly begins).
	onStart?: (ctx: DragContext) => void;
	// Fired on each pointer move while dragging.
	onMove?: (ctx: DragContext) => void;
	// Fired when dropped (pointerup while dragging). In DROP mode the
	// active target's own onDrop fires first.
	onDrop?: (ctx: DragContext) => void;
	// Fired when cancelled (ESC / pointercancel), before onEnd.
	onCancel?: (ctx: DragContext) => void;
	// Always fired last (after drop or cancel) for teardown.
	onEnd?: (ctx: DragContext) => void;
}

/* Build a semi-transparent clone of `source` that follows the pointer.
   The real source stays in place (a consumer typically dims it). The
   ghost is `position: fixed`, pointer-events: none, sized to the source,
   and carries `.lb-dnd-ghost` for styling. Positioning is handled by the
   controller; this just constructs + attaches it. */
export function defaultGhost(source: HTMLElement): HTMLElement {
	const rect = source.getBoundingClientRect();
	const ghost = source.cloneNode(true) as HTMLElement;
	ghost.classList.add(GHOST_CLASS);
	ghost.style.position = 'fixed';
	ghost.style.left = `${rect.left}px`;
	ghost.style.top = `${rect.top}px`;
	ghost.style.width = `${rect.width}px`;
	ghost.style.height = `${rect.height}px`;
	ghost.style.margin = '0';
	ghost.style.pointerEvents = 'none';
	ghost.style.zIndex = '2147483000';
	document.body.appendChild(ghost);
	return ghost;
}

/* Begin a drag from a pointerdown handler. Returns immediately; the
   lifecycle runs via capture-phase document listeners until drop or
   cancel. Safe to call from any element's pointerdown — guards the
   primary button. */
export function beginDrag(event: PointerEvent, source: HTMLElement, options: DragOptions): void {
	if (event.button !== 0) {
		return;
	}
	event.preventDefault();
	event.stopPropagation();

	const threshold = options.threshold ?? DEFAULT_THRESHOLD;
	const escCancels = options.escCancels ?? true;
	const startX = event.clientX;
	const startY = event.clientY;
	// Where within the source the pointer grabbed — keeps the ghost
	// anchored under the cursor instead of jumping to the source origin.
	const sourceRect = source.getBoundingClientRect();
	const grabOffsetX = startX - sourceRect.left;
	const grabOffsetY = startY - sourceRect.top;

	const ctx: DragContext = {
		source,
		startX,
		startY,
		clientX: startX,
		clientY: startY,
		deltaX: 0,
		deltaY: 0,
		ghost: null,
		activeTarget: null,
		event,
	};

	let dragging = false;
	let priorBodyCursor = '';

	const positionGhost = (): void => {
		if (ctx.ghost) {
			ctx.ghost.style.left = `${ctx.clientX - grabOffsetX}px`;
			ctx.ghost.style.top = `${ctx.clientY - grabOffsetY}px`;
		}
	};

	const updateActiveTarget = (): void => {
		if (!options.registry) {
			return;
		}
		const next = options.registry.hitTest(ctx);
		if (next === ctx.activeTarget) {
			return;
		}
		const prev = ctx.activeTarget;
		if (prev) {
			prev.element.classList.remove(DROP_ACTIVE_CLASS);
			prev.onLeave?.(ctx);
		}
		ctx.activeTarget = next;
		if (next) {
			next.element.classList.add(DROP_ACTIVE_CLASS);
			next.onEnter?.(ctx);
		}
	};

	const startDrag = (): void => {
		dragging = true;
		if (options.createGhost) {
			ctx.ghost = options.createGhost(source);
			positionGhost();
		}
		if (options.cursor) {
			priorBodyCursor = document.body.style.cursor;
			document.body.style.cursor = options.cursor;
		}
		options.onStart?.(ctx);
	};

	const teardown = (): void => {
		document.removeEventListener('pointermove', onMove, true);
		document.removeEventListener('pointerup', onUp, true);
		document.removeEventListener('pointercancel', onCancel, true);
		document.removeEventListener('keydown', onKey, true);
		if (ctx.activeTarget) {
			ctx.activeTarget.element.classList.remove(DROP_ACTIVE_CLASS);
		}
		if (ctx.ghost && ctx.ghost.parentNode) {
			ctx.ghost.parentNode.removeChild(ctx.ghost);
		}
		if (options.cursor) {
			document.body.style.cursor = priorBodyCursor;
		}
	};

	const onMove = (moveEvent: PointerEvent): void => {
		moveEvent.preventDefault();
		ctx.clientX = moveEvent.clientX;
		ctx.clientY = moveEvent.clientY;
		ctx.deltaX = moveEvent.clientX - startX;
		ctx.deltaY = moveEvent.clientY - startY;
		ctx.event = moveEvent;
		if (!dragging) {
			if (Math.hypot(ctx.deltaX, ctx.deltaY) < threshold) {
				return;
			}
			startDrag();
		}
		positionGhost();
		updateActiveTarget();
		options.onMove?.(ctx);
	};

	const finishDrop = (upEvent: PointerEvent): void => {
		ctx.clientX = upEvent.clientX;
		ctx.clientY = upEvent.clientY;
		ctx.event = upEvent;
		// Settle the active target against the final pointer position.
		updateActiveTarget();
		if (ctx.activeTarget) {
			ctx.activeTarget.onDrop?.(ctx);
		}
		options.onDrop?.(ctx);
		options.onEnd?.(ctx);
		teardown();
	};

	const finishCancel = (): void => {
		options.onCancel?.(ctx);
		options.onEnd?.(ctx);
		teardown();
	};

	const onUp = (upEvent: PointerEvent): void => {
		if (!dragging) {
			// Never crossed the threshold — a click, not a drag. No-op.
			teardown();
			return;
		}
		finishDrop(upEvent);
	};

	const onCancel = (): void => {
		if (!dragging) {
			teardown();
			return;
		}
		finishCancel();
	};

	const onKey = (keyEvent: KeyboardEvent): void => {
		if (keyEvent.key === 'Escape' && escCancels && dragging) {
			keyEvent.preventDefault();
			keyEvent.stopPropagation();
			finishCancel();
		}
	};

	document.addEventListener('pointermove', onMove, true);
	document.addEventListener('pointerup', onUp, true);
	document.addEventListener('pointercancel', onCancel, true);
	document.addEventListener('keydown', onKey, true);
}
